import React, { useState } from "react";
import { Menu, MenuButton as HeadlessMenuButton, MenuItems, MenuItem } from "@headlessui/react";
import { useFloating, offset, flip, shift, FloatingPortal, autoUpdate } from "@floating-ui/react";
import Spinner from "../Spinner";
import { ChevronDownIcon } from "../../icons";

export enum ButtonStyle {
  PRIMARY = "PRIMARY",
  SECONDARY = "SECONDARY",
  TERTIARY = "TERTIARY",
  DANGER = "DANGER",
}

export const styles: { [key in ButtonStyle]: string } = {
  PRIMARY:
    "text-white bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-500 disabled:opacity-90 focus:ring-emerald-600",
  SECONDARY: "text-white bg-gray-900 hover:bg-black disabled:bg-gray-900 disabled:opacity-50 focus:ring-emerald-600",
  TERTIARY: "border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 focus:ring-emerald-600",
  DANGER:
    "text-white border border-white bg-red-600 hover:bg-red-700 disabled:bg-red-400 disabled:opacity-50 focus:ring-red-500",
};

type ButtonProps = {
  text: string | React.ReactNode;
  className?: string;
  type?: ButtonStyle;
  to?: string; // If provided, the button is semantically a link (an <a>, follows accessibility best practices and triggers a full page navigation)
  onClick?: () => void; // If provided, the button is semantically a button (a <button>, follows accessibility best practices and triggers a full page navigation)
  disabled?: boolean;
  submit?: boolean;
  autoFocus?: boolean;
  menuItems?: MenuItem[];
  icon?: React.ReactNode;
};

export enum MenuItemStyle {
  PRIMARY = "PRIMARY",
  DANGER = "DANGER",
}

const menuItemStyles: { [key in MenuItemStyle]: string } = {
  PRIMARY: "",
  DANGER: "text-red-500",
};

export type MenuItem = {
  label: string | React.ReactNode;
  style: MenuItemStyle;
  onClick: () => void;
};

type MenuButtonProps = {
  type: ButtonStyle;
  menuItems: MenuItem[];
  icon?: React.ReactNode | undefined;
  spin?: boolean;
  disabled?: boolean;
  rounded?: "r" | undefined; // Allow only rounding the right side of the button (for when it is attached to the primary button)
};

export const MenuButton = ({ type, menuItems, icon, spin, disabled, rounded }: MenuButtonProps) => {
  const [isMenuLoading, setIsMenuLoading] = useState(false);

  const { refs, floatingStyles } = useFloating({
    placement: "bottom-end",
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  return (
    <Menu as="div" className="relative block -ml-px">
      <HeadlessMenuButton
        ref={refs.setReference}
        className={`relative inline-flex items-center px-2 h-full text-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-600 focus:z-10 ${
          styles[type]
        } ${rounded === "r" ? "rounded-r-md" : "rounded-md"}`}
        disabled={disabled}
      >
        <span className="sr-only">Open options</span>
        {isMenuLoading && spin ? (
          <Spinner size="sm" />
        ) : (
          icon || <ChevronDownIcon className="w-5 h-5" aria-hidden="true" />
        )}
      </HeadlessMenuButton>
      <FloatingPortal>
        <MenuItems
          ref={refs.setFloating}
          style={floatingStyles}
          className="z-50 w-56 bg-white rounded-md shadow-lg ring-1 ring-black/5 focus:outline-none"
        >
          <div className="w-full py-1">
            {menuItems.map((item, idx) => (
              <MenuItem key={`${idx}-${item.label}`}>
                <button
                  onClick={() => {
                    setIsMenuLoading(true);
                    item.onClick();
                  }}
                  disabled={disabled}
                  className={`ui-active:bg-gray-100 flex px-4 py-2 text-sm w-full ${menuItemStyles[item.style]}`}
                >
                  {item.label}
                </button>
              </MenuItem>
            ))}
          </div>
        </MenuItems>
      </FloatingPortal>
    </Menu>
  );
};

type InlineButtonProps = {
  text: string;
  className?: string;
  onClick: () => void;
};
export const InlineButton = ({ text, className, onClick }: InlineButtonProps) => {
  return (
    <button type="button" onClick={onClick} className={`text-sm text-green-600 hover:text-green-700 ${className}`}>
      {text}
    </button>
  );
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      text,
      className = "",
      type = ButtonStyle.PRIMARY,
      onClick,
      disabled = false,
      submit = false,
      autoFocus = false,
      menuItems,
      icon,
    },
    ref,
  ) => {
    onClick = submit ? () => null : onClick;

    return (
      <div className={`inline-flex ${className}`}>
        <button
          type={submit ? "submit" : "button"}
          onClick={onClick}
          autoFocus={autoFocus}
          disabled={disabled}
          ref={ref}
          className={`transition-colors inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed focus:z-10 ${
            styles[type]
          } ${menuItems ? "rounded-l-md" : "rounded-md"} w-full`}
        >
          {icon && <span className="className=w-4 h-4 mr-2">{icon}</span>}
          <span>{text}</span>
        </button>
        {menuItems && <MenuButton type={type} menuItems={menuItems} rounded="r" />}
      </div>
    );
  },
);

Button.displayName = "Button";

export default Button;
